import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bot,
  Cpu,
  Sparkles,
  Workflow,
  Terminal,
  Database,
  Radio,
  ShieldCheck,
  Mail,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteShell } from "@/components/SiteShell";
import { fetchAuthorProfile, getAuthorSocial } from "@/lib/author";
import ReactMarkdown from "react-markdown";

export const Route = createFileRoute("/about")({
  loader: () => fetchAuthorProfile(),
  head: ({ loaderData }) => ({
    meta: [
      { title: `About ${loaderData?.name ?? "the Author"} | BuildWithJIT` },
      {
        name: "description",
        content: loaderData?.about_seo_description ?? "About the author of BuildWithJIT.",
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      {
        property: "og:title",
        content: `About ${loaderData?.name ?? "the Author"} | BuildWithJIT`,
      },
      {
        property: "og:description",
        content: loaderData?.about_seo_description ?? "About the author of BuildWithJIT.",
      },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: "https://buildwithjit.com/about" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:secure_url", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: `About ${loaderData?.name ?? "the Author"}: BuildWithJIT`,
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      {
        name: "twitter:title",
        content: `About ${loaderData?.name ?? "the Author"} | BuildWithJIT`,
      },
      {
        name: "twitter:description",
        content: loaderData?.about_seo_description ?? "About the author of BuildWithJIT.",
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/about" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  component: About,
});

function About() {
  const profile = Route.useLoaderData();
  const social = getAuthorSocial(profile);
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: profile.name,
    jobTitle: profile.job_title,
    image: "https://buildwithjit.com/icon-512.png",
    worksFor: {
      "@type": "Organization",
      name: profile.company,
      url: "https://buildwithjit.com",
      logo: {
        "@type": "ImageObject",
        url: "https://buildwithjit.com/icon-512.png",
        width: 512,
        height: 512,
      },
      sameAs: Object.values(social),
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: profile.location.split(",")[0]?.trim(),
      addressRegion: profile.location.split(",")[1]?.trim(),
      postalCode: profile.postal_code,
      addressCountry: profile.country_code,
    },
    email: profile.email,
    sameAs: Object.values(social),
    description: profile.about_intro.replace(/\*\*/g, ""),
  };

  return (
    <SiteShell social={social}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <section className="about-page container-wide py-16 md:py-24 px-6 max-w-4xl mx-auto">
        <div className="section-kicker font-mono text-sm tracking-wider uppercase text-primary mb-3">
          ABOUT / BUILDWITHJIT & THE AUTHOR
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 leading-tight">
          Production AI systems, RAG pipelines & <em>intelligent automation.</em>
        </h1>

        <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p>{children}</p>,
              strong: ({ children }) => <strong className="text-foreground">{children}</strong>,
            }}
          >
            {profile.about_intro}
          </ReactMarkdown>

          <p>
            <strong className="text-foreground">BuildWithJIT</strong> is where I share what actually
            works in production: architectural tradeoffs, failure post-mortems, latency benchmarks,
            cost considerations, and tested code. No superficial marketing buzzwords, just
            battle-tested systems engineering.
          </p>
        </div>

        {/* What You Will Find Here */}
        <div className="my-12">
          <h2 className="text-2xl font-bold text-foreground mb-6">What you will find here</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="p-6 rounded-2xl border border-border bg-card/60 backdrop-blur-sm">
              <Bot className="text-primary mb-3" size={26} />
              <h3 className="font-semibold text-foreground text-lg mb-2">
                Production RAG &amp; Vector Search
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Empirical benchmarks comparing chunking strategies, hybrid search (BM25 + dense
                vectors), re-ranking models, and structured knowledge grounding (OKF vs naive RAG).
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-border bg-card/60 backdrop-blur-sm">
              <Radio className="text-primary mb-3" size={26} />
              <h3 className="font-semibold text-foreground text-lg mb-2">
                Voice AI Agents &amp; Streaming
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Real-world architectures for sub-500ms conversational phone bots combining
                WebSockets, speech-to-text (Whisper/Deepgram), fast LLM inference, and TTS
                (Cartesia/ElevenLabs).
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-border bg-card/60 backdrop-blur-sm">
              <Workflow className="text-primary mb-3" size={26} />
              <h3 className="font-semibold text-foreground text-lg mb-2">
                Self-Healing n8n Automation
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Enterprise workflow blueprints integrating webhooks, Redis queues, WhatsApp Cloud
                API, customer data reconciliation, and automated error recovery.
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-border bg-card/60 backdrop-blur-sm">
              <ShieldCheck className="text-primary mb-3" size={26} />
              <h3 className="font-semibold text-foreground text-lg mb-2">
                Agentic Security &amp; Compliance
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Practical implementation of CERT-In compliance advisories, prompt injection
                defenses, Model Context Protocol (MCP) tool authentication, and least-privilege
                agent design.
              </p>
            </div>
          </div>
        </div>

        {/* Experience & Core Stack */}
        <div className="my-12 p-8 rounded-2xl border border-border bg-card/40">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Core Technology Stack &amp; Experience
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <strong className="text-foreground block mb-1">AI &amp; Orchestration</strong>
              <span>
                OpenAI API, Anthropic Claude, LangChain, LlamaIndex, Model Context Protocol (MCP),
                Hugging Face, vLLM.
              </span>
            </div>
            <div>
              <strong className="text-foreground block mb-1">Vector DBs &amp; Retrieval</strong>
              <span>
                Pinecone, pgvector, Qdrant, ChromaDB, Cohere Re-ranker, FastEmbed, Hybrid BM25.
              </span>
            </div>
            <div>
              <strong className="text-foreground block mb-1">Voice &amp; Realtime Streaming</strong>
              <span>
                WebSockets, Deepgram Nova-2, OpenAI Whisper, Cartesia Sonic, Twilio / Exotel
                integration.
              </span>
            </div>
            <div>
              <strong className="text-foreground block mb-1">Backend &amp; Infrastructure</strong>
              <span>
                FastAPI (Python), Node.js / TypeScript, Docker, Redis Cloud, Oracle Autonomous
                Database, PostgreSQL, n8n.
              </span>
            </div>
          </div>
        </div>

        {/* Author Bio Card & Contact */}
        <div className="border-t border-border pt-10 mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-foreground">
              Want to discuss an AI architecture or collaborate?
            </h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <MapPin size={14} className="text-primary" /> {profile.location} • Available for
              technical consulting
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Mail size={14} className="text-primary" /> {profile.email}
            </p>
          </div>
          <Button asChild size="lg">
            <Link to="/contact">
              Work with me / Get in touch <ArrowRight size={18} className="ml-1" />
            </Link>
          </Button>
        </div>
      </section>
    </SiteShell>
  );
}
